import type { Ref } from "vue";
import router from "@/router";
import { useAppContextStore } from "@/store/app_context";
import { userSessionStore } from "@/store/user_session";

function filenameFromContentDisposition(header: string | null): string | undefined {
    if (!header) return undefined;
    const star = header.match(/filename\*=(?:UTF-8'')?([^;\s]+)/i);
    if (star?.[1]) {
        try {
            return decodeURIComponent(star[1].replace(/^"(.*)"$/, "$1"));
        } catch {
            return star[1].replace(/^"(.*)"$/, "$1");
        }
    }
    const plain = header.match(/filename="([^"]+)"/i) ?? header.match(/filename=([^;\s]+)/i);
    return plain?.[1]?.replace(/^"(.*)"$/, "$1");
}
export interface CallForAction {
    action: string;
    text: string;
}
export interface ApiMessage {
    level: string;
    text: string;
    style?: string;
    title?: string;
    call_for_action?: CallForAction;
}

export interface ApiResponse {
    messages?: ApiMessage[];
}

export interface RestResponse {
    status: number;
    json?: ApiResponse;
    textData?: string;
}

export type Methods = "GET" | "POST" | "PUT" | "DELETE";

/**
 * Web-service client
 * @param {*} parent Parent object that will holds results, messages and loading indicator.
 */
export class PClient {
    rootApi?: string;
    loading?: Ref<boolean>;
    messages?: Ref<ApiMessage[]>;
    status_code?: number = -1;
    resetMessages?: boolean = false;
    no_redirect?: boolean;
    service ?: string;

    constructor(loading?: Ref<boolean>, messages?: Ref<ApiMessage[]>,service ?: string, no_redirect?: boolean, resetMessages = true) {
        this.loading = loading;
        this.messages = messages;
        this.no_redirect = no_redirect;
        this.service = service;
        this.resetMessages = resetMessages;
    }

    /**
     * Low level query, handle loading and redirection if not auth.
     *
     * @param method
     * @param url
     * @param data
     * @param headers
     * @returns
     */
    async query(
        method: Methods,
        url: string,
        data: BodyInit | FormData | null | undefined,
        headers: HeadersInit | undefined
    ): Promise<Response> {
        this.onLoading(true);

        const httpResponse = await fetch(url, {
            method: method,
            headers: headers,
            body: data,
        });
        this.onLoading(false);
        this.status_code = httpResponse.status;
        if (router) {
            const route = router.currentRoute.value;
            if (route.name != "login") {
                if (httpResponse.status == 401 && !this.no_redirect) {
                    router.push({
                        name: "login",
                        query: { error: 401, redir_on_success: route.path },
                    });
                } else if (httpResponse.status == 402 && !this.no_redirect) {
                    router.push({
                        name: "EcrfPaywall",
                        query: { error: 402, redir_on_success: route.path },
                    });
                }
            }
        }

        return httpResponse;
    }

    /**
     * Query adapted to generic JSON WS.
     * Handle messages and non JSON responses.
     *
     * @param method
     * @param url
     * @param data
     * @returns
     */
    async queryAllTypes(method: Methods, url: string, data: unknown, queryType: string): Promise<any> {
        const user_session = userSessionStore();
        let headers = new Headers({ "Content-Type": "application/json", Authorization: "Bearer " + user_session.token });
        let body_param;
        if (queryType === "form") {
            headers = new Headers({ Accept: "application/json" });
            body_param = data ? (data as BodyInit) : null;
        } else {
            body_param = data ? JSON.stringify(data) : null;
        }
        const httpResponse = await this.query(method, url, body_param, headers);
        const contentType = httpResponse.headers.get("Content-type");
        if (contentType && contentType.startsWith("application/json")) {
            try {
                const json = await httpResponse.json();

                if (!json) {
                    return this.handleText(httpResponse.ok, await httpResponse.text());
                }

                this.setMessages(json?.messages);
                if (httpResponse.status > 199 && httpResponse.status < 300) {
                    return Promise.resolve(json);
                }
                return Promise.reject(json);
            } catch (err) {
                return this.handleError(err);
            }
        }

        return this.handleText(httpResponse.ok, await httpResponse.text());
    }

    handleText(isOk: boolean, text: string): Promise<any> {
        if (isOk) {
            return Promise.resolve({ text: text });
        }
        this.setMessages([{ level: "error", text: "Error " + text }]);
        return Promise.reject();
    }

    handleError(error: unknown): Promise<ApiResponse> {
        this.onLoading(false);

        if (Array.isArray(error)) {
            this.setMessages(error);
        } else {
            const message = error instanceof Error ? error.message : String(error);
            this.setMessages([{ level: "error", text: "Error " + message }]);
        }

        return Promise.reject(error);
    }

    setMessages(input: ApiMessage[] | undefined): void {
        if (this.messages) {
            if (this.resetMessages) {
                this.messages.value = [];
            }
            const m_arr = input;
            if (m_arr) {
                this.messages.value.push(...m_arr);
            }
        }
    }

    onLoading(isLoading: boolean): void {
        if (this.loading) {
            this.loading.value = isLoading;
        }
    }

    async getWsUrl(
        service: string,
        urlParams: Record<string, string | number | boolean | number[] | string[]> | undefined | null
    ): Promise<string> {
        if (this.rootApi === undefined) {
            const app_context = useAppContextStore();
            if (!app_context.loaded) {
                await app_context.load_app_context();
            }
            switch (this.service) {
                case "automl":
                    this.rootApi = app_context.remote_automl_url();
                    break;
                case "stats":
                    this.rootApi = app_context.remote_stats_url();
                    break;
                default:
                    this.rootApi = app_context.remote_automl_url();
                    break;
            }
        }
        return this.rootApi + "/" + service + this.toQueryString(urlParams);
    }

    toQueryString(arr: Record<string, string | number | boolean | number[] | string[]> | undefined | null): string {
        if (!arr) return "";
        const params = [] as string[];
        Object.keys(arr)
            .filter((key) => typeof arr[key] != "undefined")
            ?.forEach((key) => {
                const k = `${key}=`,
                    v = arr[key];
                if (v === null) return;
                if (Array.isArray(v)) {
                    v?.forEach((v2) => {
                        params.push(k + encodeURIComponent(v2.toString()));
                    });
                } else {
                    params.push(k + encodeURIComponent(v.toString()));
                }
            });
        return "?" + params.join("&");
    }

    async queryWs<T>(
        method: Methods,
        service: string,
        urlParams?: Record<string, string | number | boolean | number[] | string[]> | null,
        body?: unknown,
        queryType = "json"
    ): Promise<T> {
        return this.queryAllTypes(method, await this.getWsUrl(service, urlParams), body, queryType);
    }

    /**
     * GET a binary file from the WS root (stats/automl) with JWT, then trigger a browser download.
     * JSON responses are treated as API errors; messages are populated and the method returns false.
     */
    async downloadWsBlob(
        servicePath: string,
        urlParams: Record<string, string | number | boolean | number[] | string[]> | undefined | null,
        defaultFilename: string
    ): Promise<boolean> {
        const user_session = userSessionStore();
        try {
            const url = await this.getWsUrl(servicePath, urlParams);
            const httpResponse = await this.query("GET", url, null, {
                Authorization: "Bearer " + user_session.token,
            });
            const contentType = httpResponse.headers.get("Content-Type") ?? "";
            if (contentType.includes("application/json")) {
                try {
                    const json = (await httpResponse.json()) as { messages?: ApiMessage[]; text?: string };
                    this.setMessages(json.messages ?? [{ level: "error", text: json.text ?? "Download failed" }]);
                } catch {
                    this.setMessages([{ level: "error", text: "Download failed" }]);
                }
                return false;
            }
            if (!httpResponse.ok) {
                this.setMessages([{ level: "error", text: `Download failed (${httpResponse.status})` }]);
                return false;
            }
            const blob = await httpResponse.blob();
            const objectUrl = URL.createObjectURL(blob);
            try {
                const a = document.createElement("a");
                a.href = objectUrl;
                a.download =
                    filenameFromContentDisposition(httpResponse.headers.get("Content-Disposition")) ?? defaultFilename;
                document.body.appendChild(a);
                a.click();
                a.remove();
            } finally {
                URL.revokeObjectURL(objectUrl);
            }
            return true;
        } catch {
            this.setMessages([{ level: "error", text: "Download failed" }]);
            return false;
        }
    }
}
